import React, { useContext, useState } from 'react'
import styled, { keyframes, css } from 'styled-components'
import { FiSave } from 'react-icons/fi'
import { GrEdit, GrTrash } from 'react-icons/gr'
import AppContext from 'context'
import NumDisplay from 'components/num-display'
import ToolTip from 'components/tooltip'
import { keyExists, moneyFormat } from 'utils/helpers'
import colors from 'utils/colors'
import mixins from 'utils/mixins'
import WP_DATA from 'utils/wp_data'

const initialIconBkdgCol = 'rgba(0,0,0,.14)';

const pulse = keyframes`
  0% {
    background-color: ${initialIconBkdgCol};
  }
  50% {
    background-color: ${colors.blue};
  }
  100% {
    background-color: ${initialIconBkdgCol};
  }
`

const pulseAnimation = css`${pulse} 2s ease-in-out infinite`

const LightBackground = styled.div`
  ${mixins.lightBackground}
`

const Edit = styled.button`
  display: ${({ open }) => open ? 'block': 'none'};
  animation: ${({ open }) => open ? pulseAnimation : 'none'};
  position: absolute;
  top: -24px;
  border-radius: 50px;
  right: -24px;
  background-color: ${initialIconBkdgCol};
  color: ${({ open }) => open ? 'white': 'black'};
  &:hover {
    color: white;
    background-color: ${colors.blue};
  }
`

const FieldSet = styled.div`
  ${mixins.card}
  border-top: 2px solid ${props => props.sectionId === 'Income' ? colors.green : colors.blue};
  position: relative;
  &:hover ${Edit} {
    display: block;
  }
`

const Header = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  cursor: pointer;
`

const Legend = styled.legend`
  display: flex;
  justify-content: flex-start;
`

const Controls = styled.div`
  border-top: .5px solid ${colors.grey};
  padding: 30px 0;
  margin-top: 30px;
  button:disabled {
    opacity: 0.7;
  }
`

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-gap: 60px;
`

const Delete = styled.button`
  background: red;
  color: white;
  margin: 60px auto 0; 
  display: block;
  width: auto;
`

const RadiosLayout = styled.div`
  display: flex;
  & > input { margin-right: 8px; }
  & > label { margin-right: 12px; }
`

export default ({ sectionId, id, fields }) => {
  const {
    current,
    projected,
    frequency,
    notes,
    // salary
    salary_type,
    num_weekly_hours,
    avg_weekly_overtime_hours,
    // soc. security
    age_started,
    anticipated_62,
    anticipated_67,
    anticipated_70,
    // Loans
    interest_rate,
    years_remaining,
    outstanding_balance,
    fixed_or_variable,
    terms,
    // pension
    survivorship,
    survivorship_type,
    // retirement
    pre_tax_contributions,
    after_tax_contributions,
  } = fields

  const { dispatch, save } = useContext(AppContext)
  const [open, setOpen] = useState(false)
  const [newCardTitle, setNewCardTitle] = useState('')
  const isDefaultField = WP_DATA.default_keys.includes(id)

  const handleInputChange = e => {
    let { name, value, type } = e.target || e
    if (!value && type === 'number')
      value = 0
    else if (type === 'number')
      value = parseFloat(value)
    dispatch({
      type: 'CALC_SECTION',
      payload: { sectionId, id, name, value }
    })
  }

  const finalCurrent = frequency === 'Annually' ? current / 12 : current;
  const finalProjected = frequency === 'Annually' ? projected / 12 : projected;

  const handleFrequencyChange = e => {
    const { dataset, value, type } = e.target
    handleInputChange({
      value,
      type,
      name: dataset.name,
    })
  }

  const toggleAndSave = () => {
    if (open) save()
    setOpen(!open)
  }

  return (
    <FieldSet sectionId={sectionId}>
      <Edit onClick={toggleAndSave} open={open} type="button">
        {open ? <FiSave /> : <GrEdit />}
      </Edit>

      <Legend>
        <h3 style={{ marginTop: 5, marginRight: 5 }}>{id}</h3>
        {Object.keys(WP_DATA.DESCRIPTIONS).includes(id) && (
          <ToolTip title={id}>{WP_DATA.DESCRIPTIONS[id]}</ToolTip>
        )}
      </Legend>

      <Header onClick={toggleAndSave}>
        <NumDisplay
          bigNum={`${moneyFormat.to(finalCurrent)}/mo`}
          heading="current"
          style={{ textAlign: 'center' }}
        />
        <NumDisplay
          bigNum={`${moneyFormat.to(finalProjected)}/mo`}
          heading="projected"
          style={{ textAlign: 'center' }}
        />
      </Header>

      <div>
        {/* Social Security */}
        {keyExists('survivorship', fields) && (
          <span><strong>Survivorship:</strong> {survivorship_type === '$' && survivorship_type}{survivorship}{survivorship_type === '%' && survivorship_type}/survivor</span>
        )}

        {/* Pension */}
        {keyExists('age_started', fields) && (
          <>
            <LightBackground>
              <strong>Age started:</strong> {age_started}<br />
            </LightBackground>
            OR<br/>
            <LightBackground>
              <strong>Anticipated at ages</strong>{' '}<br />
              <strong>62:</strong> {moneyFormat.to(anticipated_62)}{' '}<br />
              <strong>67:</strong> {moneyFormat.to(anticipated_67)}{' '}<br />
              <strong>70:</strong> {moneyFormat.to(anticipated_70)}{' '}
            </LightBackground>
          </>
        )}

        {notes && (
          <p style={{ fontSize: 14 }}>
            <strong>Notes</strong><br/>
            {notes}
          </p>
        )}
      </div>

      {/* Retirement */}
      {keyExists('pre_tax_contributions', fields) && (
        <><span><strong>Pre-Tax Contributions:</strong> {moneyFormat.to(pre_tax_contributions)}</span><br /></>
      )}
      {keyExists('after_tax_contributions', fields) && (
        <span><strong>Post-Tax Contributions:</strong> {moneyFormat.to(after_tax_contributions)}</span>
      )}

      {/* Salary */}
      {keyExists('salary_type', fields) && (
        <><strong>Salary Type:</strong> {salary_type}<br /></>
      )}
      {keyExists('num_weekly_hours', fields) && (
        <><strong>Weekly Hours:</strong> {num_weekly_hours}<br /></>
      )}
      {keyExists('avg_weekly_overtime_hours', fields) && (
        <><strong>Avg. Weekly Overtime Hours:</strong> {avg_weekly_overtime_hours}<br /></>
      )}

      {/* Loans */}
      {keyExists('interest_rate', fields) && (
        <><strong>Interest Rate:</strong> {interest_rate}%<br /></>
      )}
      {keyExists('years_remaining', fields) && (
        <><strong>Years Remaining:</strong> {years_remaining}<br /></>
      )}
      {keyExists('outstanding_balance', fields) && (
        <><strong>Outstanding Balance:</strong> {moneyFormat.to(outstanding_balance)}<br /></>
      )}
      {keyExists('fixed_or_variable', fields) && (
        <><strong>Fixed or Variable:</strong> {fixed_or_variable}<br /></>
      )}
      {keyExists('terms', fields) && (
        <><strong>Terms:</strong> {terms}<br /></>
      )}

      {open && (
        <Controls>
          <Grid>
            <div>
              <label>
                <strong>Current</strong>
                <input
                  name="current"
                  value={current}
                  onChange={handleInputChange}
                  type="number"
                />
              </label>
            </div>
            <div>
              <label>
                <strong>Projected</strong>
                <input
                  name="projected"
                  value={projected}
                  onChange={handleInputChange}
                  type="number"
                />
              </label>
            </div>
          </Grid>
          <br />

          <RadiosLayout>
            <label><strong>Frequency</strong></label>
            <input
              name={`frequency-${id}`}
              data-name="frequency"
              type="radio"
              value="Monthly"
              checked={frequency === 'Monthly'}
              onChange={handleFrequencyChange}
            />
            <label>Monthly</label>
            <input
              name={`frequency-${id}`}
              data-name="frequency"
              type="radio"
              value="Annually"
              checked={frequency === 'Annually'}
              onChange={handleFrequencyChange}
            />
            <label>Annually</label>
          </RadiosLayout>
          {keyExists('pre_tax_contributions', fields) && (
            <>
              <label>
                <strong>Pre Tax Contributions</strong>{' '}
                <input
                  name="pre_tax_contributions"
                  value={pre_tax_contributions}
                  onChange={handleInputChange}
                  type="number"
                />
              </label>
              <br />
            </>
          )}
          {keyExists('after_tax_contributions', fields) && (
            <>
              <label>
                <strong>After Tax Contributions</strong>{' '}
                <input
                  name="after_tax_contributions"
                  value={after_tax_contributions}
                  onChange={handleInputChange}
                  type="number"
                />
              </label>
              <br />
            </>
          )}
          {keyExists('age_started', fields) && (
            <LightBackground>
              <label>
                <strong>Age Started</strong>{' '}
                <input
                  name="age_started"
                  value={age_started}
                  onChange={handleInputChange}
                  type="number"
                />
              </label>
              <br />
            </LightBackground>
          )}
          
          {(keyExists('anticipated_62', fields) || keyExists('anticipated_67', fields) || keyExists('anticipated_70', fields)) && (
            <>
              OR
              <LightBackground>
                {keyExists('anticipated_62', fields) && (
                  <>
                    <label>
                      <strong>Anticipated 62</strong>{' '}
                      <input
                        name="anticipated_62"
                        value={anticipated_62}
                        onChange={handleInputChange}
                        type="number"
                      />
                    </label>
                    <br />
                  </>
                )}
                {keyExists('anticipated_67', fields) && (
                  <>
                    <label>
                      <strong>Anticipated 67</strong>{' '}
                      <input
                        name="anticipated_67"
                        value={anticipated_67}
                        onChange={handleInputChange}
                        type="number"
                      />
                    </label>
                    <br />
                  </>
                )}
                {keyExists('anticipated_70', fields) && (
                  <>
                    <label>
                      <strong>Anticipated 70</strong>{' '}
                      <input
                        name="anticipated_70"
                        value={anticipated_70}
                        onChange={handleInputChange}
                        type="number"
                      />
                    </label>
                    <br />
                  </>
                )}
              </LightBackground>
            </>
          ) }

          {/* Pension */}
          {keyExists('survivorship', fields) && (
            <>
              <label>
                <strong>Survivorship (Amt/survivor)</strong>
                <input
                  name="survivorship"
                  value={survivorship}
                  onChange={handleInputChange}
                  type="number"
                />
              </label>
              <br />
            </>
          )}
          {keyExists('survivorship_type', fields) && (
            <>
              <RadiosLayout>
                <label><strong>Survivorship Type</strong></label>
                <input
                  name={`survivorship_type-${id}`}
                  data-name="survivorship_type"
                  type="radio"
                  value="%"
                  checked={survivorship_type === '%'}
                  onChange={handleFrequencyChange}
                />
                <label>%</label>
                <input
                  name={`survivorship_type-${id}`}
                  data-name="survivorship_type"
                  type="radio"
                  value="$"
                  checked={survivorship_type === '$'}
                  onChange={handleFrequencyChange}
                />
                <label>$</label>
              </RadiosLayout>
              <br />
            </>
          )}

          {keyExists('salary_type', fields) && (
            <>
              <RadiosLayout>
                <label><strong>Salary Type</strong></label><br/>
                <input
                  name={`salary_type-${id}`}
                  data-name="salary_type"
                  type="radio"
                  value="Salary"
                  checked={salary_type === 'Salary'}
                  onChange={handleFrequencyChange}
                />
                <label>Salary</label>
                <input
                  name={`salary_type-${id}`}
                  data-name="salary_type"
                  type="radio"
                  value="Hourly"
                  checked={salary_type === 'Hourly'}
                  onChange={handleFrequencyChange}
                />
                <label>Hourly</label>
              </RadiosLayout>
              <br />
            </>
          )}
          {keyExists('num_weekly_hours', fields) && (
            <>
              <label>
                <strong>Num. Weekly Hours</strong><br/>
                <input
                  name="num_weekly_hours"
                  value={num_weekly_hours}
                  onChange={handleInputChange}
                  type="number"
                />
              </label>
              <br />
            </>
          )}
          {keyExists('avg_weekly_overtime_hours', fields) && (
            <>
              <label>
                <strong>Avg. Weekly Overtime Hours</strong><br/>
                <input
                  name="avg_weekly_overtime_hours"
                  value={avg_weekly_overtime_hours}
                  onChange={handleInputChange}
                  type="number"
                />
              </label>
              <br />
            </>
          )}
          {/* Loans */}
          {keyExists('interest_rate', fields) && (
            <>
              <label>
                <strong>Interest Rate</strong><br/>
                <input
                  name="interest_rate"
                  value={interest_rate}
                  onChange={handleInputChange}
                  type="number"
                />
              </label>
              <br />
            </>
          )}
          {keyExists('years_remaining', fields) && (
            <>
              <label>
                <strong>Years Remaining</strong><br/>
                <input
                  name="years_remaining"
                  value={years_remaining}
                  onChange={handleInputChange}
                  type="number"
                />
              </label>
              <br />
            </>
          )}
          {keyExists('outstanding_balance', fields) && (
            <>
              <label>
                <strong>Outstanding Balance</strong><br/>
                <input
                  name="outstanding_balance"
                  value={outstanding_balance}
                  onChange={handleInputChange}
                  type="number"
                />
              </label>
              <br />
            </>
          )}
          {keyExists('fixed_or_variable', fields) && (
            <>
              <RadiosLayout>
                <label><strong>Fixed or Variable</strong></label><br/>
                <input
                  name={`fixed_or_variable-${id}`}
                  data-name="fixed_or_variable"
                  type="radio"
                  value="Fixed"
                  checked={fixed_or_variable === 'Fixed'}
                  onChange={handleFrequencyChange}
                />
                <label>Fixed</label>
                <input
                  name={`fixed_or_variable-${id}`}
                  data-name="fixed_or_variable"
                  type="radio"
                  value="Variable"
                  checked={fixed_or_variable === 'Variable'}
                  onChange={handleFrequencyChange}
                />
                <label>Variable</label>
              </RadiosLayout>
              <br />
            </>
          )}
          {keyExists('terms', fields) && (
            <>
              <strong>Terms</strong> 
              <br />
              <textarea
                name="terms"
                value={terms}
                onChange={handleInputChange}
                placeholder="Terms..."
              />
            </> 
          )}

          <br />
          <label htmlFor="notes"><strong>Notes</strong></label><br/>
          <textarea
            name="notes"
            value={notes}
            onChange={handleInputChange}
            placeholder="Notes..."
          />
          {!isDefaultField && (
            <>
              <label>
                <strong>Title</strong><br/>
                <input
                  name="years_remaining"
                  value={!!newCardTitle ? newCardTitle : id}
                  onChange={e => setNewCardTitle(e.target.value)}
                  type="text"
                />
              </label>
              <button 
                disabled={id === newCardTitle || newCardTitle === ''}
                onClick={e => {
                  e.stopPropagation()
                  dispatch({ 
                    type: 'UPDATE_CARD_TITLE', 
                    payload: { 
                      sectionId, 
                      id,
                      newCardTitle,
                    } 
                  })
                  setNewCardTitle('')
                  save()
                }}
              >
                Update Title
              </button>
              <br/>
              <Delete
                onClick={() => dispatch({ type: 'DELETE', payload: { sectionId, id } })}
                type="button"
              >
                <GrTrash color="white" /> Delete {id}
              </Delete>
            </>
          )}
        </Controls>
      )}
    </FieldSet>
  )
}

